import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Filter, X, Check, Search } from 'lucide-react';

/**
 * SortableHeader — Drop-in replacement for plain <th> elements.
 * Provides clickable sort (A-Z / Z-A / clear) and multi-select checklist filter popover.
 *
 * Props:
 *   label: string — column display name
 *   columnKey: string — data key for sorting/filtering
 *   sortColumn: string|null — currently sorted column
 *   sortDirection: 'asc'|'desc'
 *   onSort: (columnKey) => void
 *   filterValue: Set<string> | null — currently selected filter values
 *   onFilter: (columnKey, selectedSet) => void
 *   allData: array — full (unfiltered) data to extract unique values from
 *   filterable: boolean — whether to show filter icon (default true)
 *   className: string — extra classes for <th>
 */
export default function SortableHeader({
    label,
    columnKey,
    sortColumn,
    sortDirection,
    onSort,
    filterValue,
    onFilter,
    allData = [],
    filterable = true,
    className = '',
}) {
    const [showFilter, setShowFilter] = useState(false);
    const [search, setSearch] = useState('');
    const popoverRef = useRef(null);
    const isActive = sortColumn === columnKey;
    const hasFilter = filterValue && filterValue.size > 0;

    // Extract unique non-empty values for this column from allData
    const uniqueValues = useMemo(() => {
        const vals = new Set();
        (allData || []).forEach(row => {
            const v = (row[columnKey] ?? '').toString().trim();
            if (v && v !== '-') vals.add(v);
        });
        return [...vals].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }, [allData, columnKey]);

    // Filtered list based on search
    const filteredValues = useMemo(() => {
        if (!search) return uniqueValues;
        const q = search.toLowerCase();
        return uniqueValues.filter(v => v.toLowerCase().includes(q));
    }, [uniqueValues, search]);

    useEffect(() => {
        if (!showFilter) return;
        const handleClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setShowFilter(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showFilter]);

    const toggleValue = (val) => {
        const next = new Set(filterValue || []);
        if (next.has(val)) next.delete(val);
        else next.add(val);
        onFilter(columnKey, next.size > 0 ? next : null);
    };

    const selectAll = () => {
        onFilter(columnKey, null);
    };

    const selectNone = () => {
        // Select only what's currently visible (filtered by search) as excluded
        // Actually: "select none" means filter to show nothing — but more useful is "deselect all"
        onFilter(columnKey, null);
    };

    return (
        <th className={`text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider relative ${className}`}>
            <div className="flex items-center gap-1">
                {/* Sort button */}
                <button
                    onClick={() => onSort(columnKey)}
                    className="group inline-flex items-center gap-1 hover:text-stone-900 transition-colors"
                >
                    {label}
                    {isActive ? (
                        sortDirection === 'asc'
                            ? <ArrowUp className="w-3.5 h-3.5 text-stone-900" />
                            : <ArrowDown className="w-3.5 h-3.5 text-stone-900" />
                    ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                    )}
                </button>

                {/* Filter button */}
                {filterable && onFilter && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowFilter(!showFilter); setSearch(''); }}
                        className={`p-0.5 rounded transition-colors ${
                            hasFilter
                                ? 'text-stone-900 bg-stone-200'
                                : 'text-stone-300 hover:text-stone-500'
                        }`}
                    >
                        <Filter className="w-3 h-3" />
                    </button>
                )}
            </div>

            {/* Filter popover — multi-select checklist */}
            {showFilter && (
                <div
                    ref={popoverRef}
                    className="absolute top-full left-0 mt-1 z-50 bg-white border border-stone-200 rounded-lg shadow-lg min-w-[200px] max-w-[260px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Search within options */}
                    {uniqueValues.length > 8 && (
                        <div className="p-2 border-b border-stone-100">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-400" />
                                <input
                                    autoFocus
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-stone-200 rounded focus:outline-none focus:ring-1 focus:ring-stone-900/20"
                                    onKeyDown={(e) => { if (e.key === 'Escape') { setShowFilter(false); setSearch(''); } }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Select all / Clear */}
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-stone-100">
                        <button
                            onClick={selectAll}
                            className="text-[10px] text-stone-500 hover:text-stone-700 font-medium"
                        >
                            Select All
                        </button>
                        {hasFilter && (
                            <button
                                onClick={() => { onFilter(columnKey, null); setShowFilter(false); setSearch(''); }}
                                className="text-[10px] text-red-500 hover:text-red-700 font-medium flex items-center gap-0.5"
                            >
                                <X className="w-2.5 h-2.5" /> Clear
                            </button>
                        )}
                    </div>

                    {/* Options list */}
                    <div className="max-h-[220px] overflow-y-auto py-1">
                        {filteredValues.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-stone-400 italic">No values</p>
                        ) : (
                            filteredValues.map(val => {
                                const isChecked = !hasFilter || filterValue.has(val);
                                return (
                                    <button
                                        key={val}
                                        onClick={() => toggleValue(val)}
                                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-stone-50 transition-colors"
                                    >
                                        <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                            isChecked
                                                ? 'bg-stone-900 border-stone-900 text-white'
                                                : 'border-stone-300'
                                        }`}>
                                            {isChecked && <Check className="w-2.5 h-2.5" />}
                                        </span>
                                        <span className="truncate text-stone-700">{val}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Active filter count */}
                    {hasFilter && (
                        <div className="px-2.5 py-1.5 border-t border-stone-100 text-[10px] text-stone-400">
                            Showing {filterValue.size} of {uniqueValues.length}
                        </div>
                    )}
                </div>
            )}
        </th>
    );
}


/**
 * useTableSort — Hook that provides sort state + handler + sorted data.
 */
export function useTableSort(data, options = {}) {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = (column) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') setSortDirection('desc');
            else { setSortColumn(null); setSortDirection('asc'); }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const numericKeys = new Set(options.numericKeys || []);

    const sortedData = useMemo(() => {
        if (!sortColumn || !data) return data;
        return [...data].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            if (numericKeys.has(sortColumn)) {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            aVal = (aVal || '').toString().toLowerCase();
            bVal = (bVal || '').toString().toLowerCase();
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortColumn, sortDirection]);

    return { sortColumn, sortDirection, handleSort, sortedData };
}


/**
 * useColumnFilters — Hook that provides per-column multi-select filter state + filtered data.
 *
 * Each filter value is a Set<string> of selected values. null = no filter (show all).
 */
export function useColumnFilters(data) {
    const [columnFilters, setColumnFilters] = useState({});

    const setColumnFilter = (key, valueSet) => {
        setColumnFilters(prev => {
            const next = { ...prev };
            if (valueSet && valueSet.size > 0) next[key] = valueSet;
            else delete next[key];
            return next;
        });
    };

    const filteredData = useMemo(() => {
        const activeFilters = Object.entries(columnFilters).filter(([, v]) => v && v.size > 0);
        if (activeFilters.length === 0) return data;
        return data.filter(row =>
            activeFilters.every(([key, selected]) => {
                const cell = (row[key] ?? '').toString().trim();
                return selected.has(cell);
            })
        );
    }, [data, columnFilters]);

    return { columnFilters, setColumnFilter, filteredData };
}


/**
 * usePagination — Hook that provides client-side pagination.
 *
 * Usage:
 *   const { page, pageSize, setPage, setPageSize, pageData, totalPages, total } = usePagination(data, 25);
 */
export function usePagination(data, initialPageSize = 25) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // Reset to page 1 when data changes
    const dataLength = data?.length || 0;
    useEffect(() => { setPage(1); }, [dataLength]);

    const total = dataLength;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);

    const pageData = useMemo(() => {
        if (!data) return [];
        const start = (safePage - 1) * pageSize;
        return data.slice(start, start + pageSize);
    }, [data, safePage, pageSize]);

    return {
        page: safePage,
        pageSize,
        setPage,
        setPageSize: (size) => { setPageSize(size); setPage(1); },
        pageData,
        totalPages,
        total,
    };
}


/**
 * TablePagination — Pagination controls to render below a table.
 */
export function TablePagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }) {
    if (total <= 10) return null; // Don't show for tiny datasets

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);

    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 bg-white">
            <div className="flex items-center gap-3">
                <span className="text-xs text-stone-500 tabular-nums">
                    Showing {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
                </span>
                <select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="text-xs border border-stone-200 rounded px-1.5 py-1 bg-white text-stone-600 focus:outline-none"
                >
                    {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n} / page</option>
                    ))}
                </select>
            </div>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(1)}
                    disabled={page <= 1}
                    className="px-2 py-1 text-xs text-stone-500 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-stone-100"
                >
                    First
                </button>
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="px-2 py-1 text-xs text-stone-500 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-stone-100"
                >
                    Prev
                </button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p;
                    if (totalPages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                    else p = page - 2 + i;
                    return (
                        <button
                            key={p}
                            onClick={() => onPageChange(p)}
                            className={`w-7 h-7 text-xs rounded transition-colors ${
                                p === page
                                    ? 'bg-stone-900 text-white font-medium'
                                    : 'text-stone-500 hover:bg-stone-100 hover:text-stone-900'
                            }`}
                        >
                            {p}
                        </button>
                    );
                })}
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="px-2 py-1 text-xs text-stone-500 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-stone-100"
                >
                    Next
                </button>
                <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={page >= totalPages}
                    className="px-2 py-1 text-xs text-stone-500 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-stone-100"
                >
                    Last
                </button>
            </div>
        </div>
    );
}
